import {
  Command,
  DowncastConversionApi,
  Element,
  ButtonView,
  toWidget,
  viewToModelPositionOutsideModelElement,
} from "ckeditor5";
import shortid from "shortid";
import { css } from "styled-components";

import { IconTextLine } from "@examspark/common-ui/dist/components/icons";

import { collectModel, getModelText } from "../util/document";

import ItemEditor from "./ItemEditor";
import { PluginWithStyles } from "./PluginWithStyles";

export const UI_BUTTON_GAP = "gap";
export const COMMAND_INSERT_GAP = "insertGap";

export class InsertGapCommand extends Command {
  value = false;

  refresh() {
    const model = this.editor.model;
    const context = model.document.selection.focus?.parent;
    const range = model.document.selection.getFirstRange();

    this.value = context?.name === "gap";
    const items = Array.from(range?.getItems() ?? []);

    this.isEnabled =
      !this.value &&
      !!context?.is("element") &&
      model.schema.checkChild(context, "gap") &&
      items.every((i) => i.is("$text") || i.is("$textProxy"));
  }

  execute() {
    if (!this.isEnabled) return;

    this.editor.model.change((writer) => {
      const range = this.editor.model.document.selection.getFirstRange();

      this.editor.model.insertContent(
        writer.createElement("gap", {
          id: shortid(),
          suggestedText: getModelText(range),
        }),
      );
    });

    this.editor.editing.view.focus();
  }
}

export default class ItemEditorGap extends PluginWithStyles {
  public static get requires() {
    return [ItemEditor];
  }

  public static buildEditorStyles = () => css`
    .gap {
      border: 1px dashed rgb(187, 187, 187);
      padding: 0.2rem;
      border-radius: 0.2rem;
      color: rgb(51, 51, 51);
      font-weight: bold;
      text-transform: uppercase;
    }
  `;

  init() {
    this.editor.model.schema.register("gap", {
      allowAttributes: ["id", "suggestedText"],
      allowWhere: "$text",
      isInline: true,
      isContent: true,
      isObject: true,
    });

    this.editor.conversion.for("upcast").elementToElement({
      view: {
        name: "span",
        classes: "gap",
      },
      model: (e, { writer }) =>
        writer.createElement("gap", {
          id: e.getAttribute("data-gap-id"),
        }),
    });

    this.editor.conversion.for("editingDowncast").elementToElement({
      model: "gap",
      view: (e, api) => {
        this.editor.editing.mapper.on(
          "viewToModelPosition",
          viewToModelPositionOutsideModelElement(
            this.editor.model,
            (viewElement) => viewElement.hasClass("gap"),
          ),
        );

        //
        // For editing downcasts, CKEditor tries to be smart about not recreating
        // DOM that isn't stale. So we probably need to update text for gaps manually.
        //
        const otherGaps = collectModel(
          e.root,
          (gap) => gap.is("element") && gap.name === "gap",
        ) as Element[];

        this.editor.editing.view.change((writer) => {
          for (let i = 0; i < otherGaps.length; i++) {
            if (otherGaps[i] === e) continue;
            const view = api.mapper.toViewElement(otherGaps[i]);
            if (view == null) continue;

            const [text, ...children] = view.getChildren();
            if (!text?.is("$text") || !!children?.length) {
              continue;
            }

            const expectedText = `Gap ${i + 1}`;
            if (text.data === expectedText) continue;

            writer.remove(text);
            writer.insert(
              writer.createPositionAt(view, 0),
              writer.createText(expectedText),
            );
          }
        });

        return toWidget(this.createViewElement(e, api), api.writer);
      },
    });

    this.editor.conversion.for("dataDowncast").elementToElement({
      model: "gap",
      view: this.createViewElement.bind(this),
    });

    const command = new InsertGapCommand(this.editor);
    this.editor.commands.add(COMMAND_INSERT_GAP, command);

    this.editor.ui.componentFactory.add(UI_BUTTON_GAP, (locale) => {
      const button = new ButtonView(locale);
      button.set({
        label: this.editor.t("Insert Gap"),
        icon: IconTextLine.Content,
        tooltip: true,
      });
      button.bind("isOn", "isEnabled").to(command, "value", "isEnabled");
      button.on("execute", () => command.execute());
      return button;
    });
  }

  private createViewElement(e: Element, { writer }: DowncastConversionApi) {
    const element = writer.createContainerElement("span", {
      class: "gap",
      "data-gap-id": e.getAttribute("id"),
    });

    const number = this.determineGapNumber(e);
    const text = writer.createText(`Gap ${number}`);
    writer.insert(writer.createPositionAt(element, 0), text);

    return element;
  }

  private determineGapNumber(e: Element) {
    let stop = false;
    const previousGaps = collectModel(e.root, (gap) => {
      if (stop) return false;
      if (gap === e) {
        stop = true;
        return false;
      }
      if (gap.is("element") && gap.name === "gap") return true;
      return false;
    });

    return previousGaps.length + 1;
  }
}
